// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import difference from 'lodash/difference';

import {
  ContentAction,
  ContentActionType,
  ContentType,
  LayoutResponse,
  LayoutRowResponse,
} from 'common/report/dashboards/types';

export const ROW_COL_SPAN = 12;

export const MAX_CELL_COL_SPAN = 12;

export const GUTTER_SIZE = 20;

export const MAX_NUM_ROW_CELLS = 4;

export interface CellItem {
  colSpan: number;
  cellId: string;
  contentId?: number | string;
  contentType?: ContentType;
}

export interface Row {
  id: string;
  minHeight: number;
  cellItems: CellItem[];
}

export type GridLayoutUpdateParams = {
  rows_order?: string[];
  rows: Array<{
    id?: string;
    temp_id?: string;
    height: number;
    cells: Array<{
      id?: string;
      temp_id?: string;
      width: number;
    }>;
  }>;
};

/**
 * this function compares new & current layout and calculates the params for turning current layout into the new layout
 * note that new and current layout should differ by at most one action for the function to calculate the params
 */
export function getLayoutUpdateParamsForHistoryAction(newLayout, currentLayout, contentAction): GridLayoutUpdateParams {
  let tempIdGenerator = -1;

  const existingCells = getLayoutCells(currentLayout);

  const adjustedLayout = cloneDeep(newLayout);
  const content = pick(contentAction, `contentId`, `contentType`);
  const addedContent =
    contentAction?.actionType === ContentActionType.Undelete || ContentActionType.UnMove ? content : null;
  const removedContent =
    contentAction?.actionType === ContentActionType.Delete || ContentActionType.Move ? content : null;

  //changing the ids used in layout cell/rows to match the current layout ids,
  // since api only accepts one new cell that will make sure we don't label existing cell as new cell
  Object.keys(adjustedLayout.rows).forEach((rowId) => {
    const row = adjustedLayout.rows[rowId];
    const rowCells = row.cells;

    rowCells.forEach((cell, idx) => {
      const identicalExistingCell = existingCells.find(
        (c) => cell.content_id === c.content_id && cell.content_type === c.content_type,
      );
      rowCells[idx].id = identicalExistingCell?.id || String(tempIdGenerator--);
    });

    const adjustedRowId =
      Object.keys(currentLayout.rows).find((id) => isEqual(currentLayout.rows[id].cells, rowCells)) ||
      String(tempIdGenerator--);

    const adjustedRow = {height: row.height, cells: rowCells};
    const rowIdx = adjustedLayout.order.findIndex((id) => id === rowId);

    delete adjustedLayout.rows[rowId];
    adjustedLayout.rows[adjustedRowId] = adjustedRow;
    adjustedLayout.order[rowIdx] = adjustedRowId;
  });

  const rowsOrder = adjustedLayout.order;
  const rows = adjustedLayout.rows;
  const rowsParam = [];
  const existingRowIds = currentLayout.order;

  const existingCellIdsWithoutRemovedContent = existingCells
    .filter(
      (cell) => cell.content_id !== removedContent?.contentId || cell.content_type !== removedContent?.contentType,
    )
    .map((cell) => cell.id);

  const adjustedLayoutCellIds = getLayoutCells(adjustedLayout).map((cell) => cell.id);

  //using ids of these removed cells for cases where we only replace the content of cell in new layout
  //because we don't want to label such contents as new, because they have already existed and deleted afterward
  //so we use these existing unused cell ids for these contents
  const removedCellIds = difference(existingCellIdsWithoutRemovedContent, adjustedLayoutCellIds);
  let removeCellIdCounter = 0;

  Object.keys(rows).forEach((rowId) => {
    if (!isEqual(currentLayout.rows[rowId], rows[rowId])) {
      let currentRowParams = {};
      const rowIdKey = Number(rowId) < 0 ? `temp_id` : `id`;
      currentRowParams[rowIdKey] = rowId;
      currentRowParams = {...currentRowParams, height: rows[rowId].height, cells: getCellsParam(rows[rowId].cells)};
      rowsParam.push(currentRowParams);
    }
  });

  //add removed rows as empty rows
  existingRowIds.forEach((rowId) => {
    if (!rowsOrder.includes(rowId)) {
      rowsParam.push({id: rowId, height: 0, cells: []});
      rowsOrder.push(rowId);
    }
  });

  function getCellsParam(cells) {
    return cells.map((cell) => {
      let cellParams = {};
      if (Number(cell.id) < 0) {
        if (cell.content_id === addedContent?.contentId && cell.content_type === addedContent?.contentType) {
          cellParams = {temp_id: cell.id, width: cell.width};
        } else {
          cellParams = {
            id: removedCellIds[removeCellIdCounter++],
            width: cell.width,
            content_id: cell.content_id,
            content_type: cell.content_type,
          };
        }
      } else {
        cellParams = {id: cell.id, width: cell.width};
      }
      return cellParams;
    });
  }
  function getLayoutCells(layout) {
    let cells = [];
    Object.values(layout.rows).forEach((row: LayoutRowResponse) => {
      const rowCells = row.cells;
      cells = [...cells, ...rowCells];
    });
    return cells;
  }

  return {rows: rowsParam, rows_order: rowsOrder};
}

export function isEphemeralContent(contentType: ContentType) {
  return contentType === ContentType.Spark;
}

export function isRedirectContent(contentAction: ContentAction) {
  return (
    contentAction.actionType === ContentActionType.Create &&
    contentAction.contentType === ContentType.Report &&
    !(`bookmark` in contentAction.contentParams)
  );
}

export function getLayoutUpdateParams(rows: Row[], rowsOrder: string[] = null): GridLayoutUpdateParams {
  // filter out ephemeral content
  const filteredRows = rows.map((row) => {
    const beforeNum = row.cellItems.length;

    let cellItems = row.cellItems.filter((cell) => !isEphemeralContent(cell.contentType));

    const contentRemoved = beforeNum > cellItems.length;
    if (contentRemoved) {
      // evenly distribute the row if there was content removed
      cellItems = cellItems.map((cell) => ({...cell, colSpan: ROW_COL_SPAN / cellItems.length}));
    }

    return {...row, cellItems};
  });

  const rowsParam = filteredRows.map((row) => {
    const rowParam = {height: row.minHeight, cells: getCellsParam(row.cellItems)};

    if (Number(row.id) < 0) {
      rowParam[`temp_id`] = row.id;
    } else {
      rowParam[`id`] = row.id;
    }

    return rowParam;
  });

  function getCellsParam(cellItems: CellItem[]) {
    return cellItems.map((cell) => {
      const cellId = parseInt(cell.cellId, 10);
      if (!Number.isNaN(cellId) && cellId < 0) {
        return {
          temp_id: `${cell.cellId}`,
          width: cell.colSpan,
        };
      }
      return {
        id: cell.cellId,
        width: cell.colSpan,
      };
    });
  }

  return {rows: rowsParam, ...(rowsOrder ? {rows_order: rowsOrder} : {})};
}

export function getLayoutParamWithContentRemoved(content, layout) {
  const {contentId, contentType} = content;

  const {row, cellIdx} = getContentGridDetails(layout, contentId, contentType);

  const cellItemsWithDeletion = removeCellFromRow(row.cellItems, cellIdx);

  const rowWithDeletedCell = {id: row.id, minHeight: row.minHeight, cellItems: cellItemsWithDeletion};

  return getLayoutUpdateParams([rowWithDeletedCell], layout.order);
}

export function addNewCellToRow(rowCellItems: CellItem[], newCell: CellItem, newCellIdx: number) {
  const numTotalCells = rowCellItems.length + 1;

  const equalSizeCellColSpan = ROW_COL_SPAN / numTotalCells;

  if (numTotalCells <= 4) {
    rowCellItems.splice(newCellIdx, 0, newCell);
  }
  rowCellItems.forEach((item) => {
    item.colSpan = equalSizeCellColSpan;
  });
  return rowCellItems;
}

export function removeCellFromRow(rowCellItems: CellItem[], removedCellIdx: number) {
  const numTotalCells = rowCellItems.length - 1;

  rowCellItems.splice(removedCellIdx, 1);

  if (rowCellItems.length) {
    const equalSizeCellColSpan = ROW_COL_SPAN / numTotalCells;
    rowCellItems.forEach((item) => {
      item.colSpan = equalSizeCellColSpan;
    });
  }

  return rowCellItems;
}

export function getContentGridDetails(
  gridLayout: LayoutResponse,
  contentId: number | string,
  contentType: ContentType,
) {
  let contentRow, contentCellIdx;
  Object.keys(gridLayout.rows).forEach((rowId) => {
    const row = gridLayout.rows[rowId];
    const rowCells = (row as any).cells;
    const cellIdx = rowCells.findIndex((cell) => cell.content_id === contentId && cell.content_type === contentType);
    if (cellIdx >= 0) {
      contentRow = {...row, id: rowId};
      contentCellIdx = cellIdx;
    }
  });
  const rowCellItems = contentRow?.cells.map((cell) => ({
    colSpan: cell.width,
    cellId: cell.id,
    contentType: cell.content_type,
    contentId: cell.content_id,
  }));
  const row = contentRow ? {id: contentRow.id, minHeight: contentRow.minHeight, cellItems: rowCellItems} : null;
  return {row, cellIdx: contentCellIdx};
}

export function getContentsInGridLayout(layout: LayoutResponse) {
  const contents = [];
  getOrderedGridRows(layout).forEach((row) => {
    row.cells.forEach((cell) => {
      const contentType = cell.content_type;
      const contentId = cell.content_id;
      contents.push({contentId, contentType});
    });
  });
  return contents;
}

export function getOrderedGridRows(layout: LayoutResponse): LayoutRowResponse[] {
  return layout?.order.map((rowId) => layout.rows[rowId]) || [];
}
